// A discourse is a TOC group, never a guessed numeric range. Full reference
// prefixes keep identical sicha numbers in different parshiyos/volumes apart.
export function discourseSections(toc, text) {
  const rows = (toc?.sections || []).filter((row) => !row.isHeader);
  const refs = [text.readerRef, text.tocTargetRef, text.ref].filter(Boolean);
  const rowRefs = (row) => [row.readerRef, row.targetRef, row.ref].filter(Boolean);
  const selected = rows.find((row) => rowRefs(row).some((ref) => refs.includes(ref)));
  if (!selected) return [];
  const key = (row) => {
    const ref = row.readerRef || row.ref || "";
    const named = ref.match(/^(.*?\b(?:Sicha|Maamar)\s+\d+)(?=,|\s+(?:Section|Seif)|$)/i);
    if (named) return named[1];
    const path = row.path || [];
    if (path.length && /section|seif|סעיף/i.test(row.label || row.heLabel || "")) return JSON.stringify(path);
    return "";
  };
  const group = key(selected);
  if (!group) return [];
  return rows.filter((row) => key(row) === group);
}

export async function fetchReadingUnit(ref, versions, fetchSection, fetchToc, onFirstUnit) {
  const selected = await fetchSection(ref, versions);
  if (!selected) return null;
  // The caller may paint this one section while the rest of the discourse is
  // still arriving. The complete text replaces it through the same publish
  // path, so nothing about the finished unit changes.
  if (onFirstUnit) { try { onFirstUnit(selected); } catch (e) {} }
  const toc = await fetchToc(selected.collection || selected.title || selected.ref);
  const rows = discourseSections(toc, selected);
  const segmentedDiscourse = [selected.readerRef, selected.ref, ref].some((value) =>
    /\b(?:Sicha|Maamar)\s+\d+.*\b(?:Section|Seif)\s+\d+/i.test(value || ""));
  // Do not mistake a failed table of contents for a complete one-section text.
  if (!rows.length && segmentedDiscourse) return null;
  if (rows.length < 2) return selected;
  const units = new Array(rows.length);
  let cursor = 0;
  await Promise.all(Array.from({ length: Math.min(4, rows.length) }, async () => {
    while (cursor < rows.length) {
      const index = cursor++;
      const row = rows[index];
      const target = row.readerRef || row.ref || row.targetRef;
      if ([selected.readerRef, selected.ref, selected.tocTargetRef].includes(target)) { units[index] = selected; continue; }
      // One transient failure must not cost the reader a whole letter, so a
      // section that comes back empty is asked for once more.
      let unit = await fetchSection(target, versions);
      if (!unit?.segments?.length) unit = await fetchSection(target, versions);
      units[index] = unit;
    }
  }));
  // A missing middle section must not silently present an incomplete discourse.
  if (units.some((unit) => !unit?.segments?.length)) return null;
  const first = units[0], last = units[units.length - 1];
  const segments = units.flatMap((unit) => unit.segments);
  const translated = segments.filter((segment) => String(segment.en || "").replace(/<[^>]*>/g, "").trim()).length;
  return { ...first, units, segments, prev: first.prev, next: last.next,
    hasEnglish: translated > 0,
    focusRef: selected.focusRef,
    translationCoverage: { translatedSegments: translated, totalSegments: segments.length, percent: Math.round(translated / segments.length * 100) },
  };
}

// Tracks one adjacent-discourse request across React effect teardown/setup.
// A navigation key change invalidates completion from the old request without
// allowing that request to overwrite the new key's status.
export function createContinuationController(load) {
  let key = null;
  let generation = 0;
  let status = 'idle';
  let pending = null;
  const reset = (nextKey) => {
    if (nextKey === key) return;
    key = nextKey;
    generation += 1;
    status = 'idle';
    pending = null;
  };
  const request = (requestKey) => {
    reset(requestKey);
    if (status === 'loading') return pending;
    if (status === 'error') return Promise.resolve(false);
    const requestGeneration = generation;
    status = 'loading';
    pending = Promise.resolve().then(() => load(requestKey)).then((result) => {
      if (requestGeneration !== generation) return false;
      status = result ? 'idle' : 'error';
      pending = null;
      return result;
    }).catch(() => {
      if (requestGeneration === generation) {
        status = 'error';
        pending = null;
      }
      return false;
    });
    return pending;
  };
  const retry = (requestKey) => {
    reset(requestKey);
    if (status === 'error') status = 'idle';
    return request(requestKey);
  };
  return {
    reset,
    request,
    retry,
    get status() { return status; },
    get key() { return key; },
  };
}

const sectionRefs = (row) => [row.readerRef, row.ref, row.targetRef].filter(Boolean);
// `readerRef` is not always the display ref: a row whose display ref repeats
// carries the bare corpus id instead, which would otherwise be mistaken for a
// book. `ref` keeps the volume label on every Toras Menachem row, so it is the
// reliable source for the owning book; the other fields remain as fallbacks.
const sectionBook = (row) => {
  const candidates = [row.ref, row.readerRef, row.targetRef].map((value) => String(value || ''));
  for (const value of candidates) {
    const named = value.match(/^(.*?\bVol(?:ume)?\.?\s*\d+)/i);
    if (named) return named[1];
  }
  // Books whose ref carries no volume number (Kuntres Umayin, Hemshech Ayin
  // Beis, …) name the book in the first comma part of `ref`, never in the bare
  // corpus id that a repeating display ref pushes into `readerRef`.
  return String(row.ref || row.readerRef || row.targetRef || '').split(',')[0];
};
const bookKey = (value) => String(value || '').trim().toLowerCase();
const discourseKey = (row) => {
  for (const value of [row.ref, row.readerRef].map((item) => String(item || ''))) {
    const named = value.match(/^(.*?\b(?:Sicha|Maamar)\s+\d+)(?=,|\s+(?:Section|Seif)|$)/i);
    if (named) return named[1];
  }
  return (row.path || []).length ? JSON.stringify(row.path) : '';
};
// Bare corpus ids spell the section as `:seif:9`, which is just as valid as
// the reader's "Section 9" label and must not fall through to a neighbouring
// field with a different number.
const sectionNumber = (row) => {
  for (const value of [row.readerRef, row.ref, row.targetRef, row.label, row.heLabel].map((item) => String(item || ''))) {
    const explicit = value.match(/(?:Section|Seif|סעיף)\s*[:#]?\s*(\d+)/i)
      || value.match(/:(?:seif|section)[: _-]*(\d+)/i);
    if (explicit) return explicit[1];
  }
  return '';
};

export function readingCoordinate(toc, ref) {
  const rows = (toc?.sections || []).filter((row) => !row.isHeader);
  const row = rows.find((item) => sectionRefs(item).some((value) => ref === value || ref.startsWith(value + ':')));
  if (!row || !sectionNumber(row) || !discourseKey(row)) return null;
  const book = sectionBook(row);
  const groups = [...new Set(rows.filter((item) => bookKey(sectionBook(item)) === bookKey(book)).map(discourseKey).filter(Boolean))];
  return { book, maamar: groups.indexOf(discourseKey(row)) + 1, seif: Number(sectionNumber(row)) };
}

export function resolveReadingCoordinate(toc, book, maamar, seif, slug) {
  const wanted = slug(book);
  const rows = (toc?.sections || []).filter((row) => {
    if (!row || row.isHeader) return false;
    const derived = slug(sectionBook(row));
    // A table of contents belongs to one book, so rows whose ref names the book
    // only in Hebrew (no slug of their own) still belong to the requested book.
    return (derived || wanted) === wanted;
  });
  const groups = [...new Set(rows.map(discourseKey).filter(Boolean))];
  const key = groups[Number(maamar) - 1];
  return key ? rows.find((row) => discourseKey(row) === key && Number(sectionNumber(row)) === Number(seif)) || null : null;
}

// Display refs can repeat across sections; focus by the corpus segment ID.
export function sectionFocus(text, target) {
  const units = text.units || [text];
  const unit = units.find(unit => [unit.readerRef, unit.tocTargetRef].includes(target))
    || units.find(unit => unit.ref === target);
  return unit?.segments?.[0]?.id || unit?.segments?.[0]?.ref || '';
}
